import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Event, EventType, EventVisibility } from '../types/database';

export function useBandEvents(bandId: string | undefined, includePrivate = false) {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    if (!bandId) return;

    let query = supabase
      .from('events')
      .select('*')
      .eq('band_id', bandId)
      .order('start_time', { ascending: true });

    if (!includePrivate) {
      query = query.eq('visibility', 'public');
    }

    const { data } = await query;
    setEvents(data ?? []);
    setLoading(false);
  }, [bandId, includePrivate]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return { events, loading, refetch: fetchEvents };
}

export function usePublicEvents(bandId: string | undefined) {
  return useBandEvents(bandId, false);
}

export function useUpcomingPublicEvents(limit = 20) {
  const [events, setEvents] = useState<(Event & { band: { name: string; slug: string; logo_url: string | null } })[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      const { data } = await supabase
        .from('events')
        .select('*, band:bands(name, slug, logo_url)')
        .eq('visibility', 'public')
        .gte('start_time', new Date().toISOString())
        .order('start_time', { ascending: true })
        .limit(limit);

      setEvents((data as typeof events) ?? []);
      setLoading(false);
    }
    fetch();
  }, [limit]);

  return { events, loading };
}

interface CreateEventInput {
  band_id: string;
  title: string;
  description?: string;
  event_type: EventType;
  visibility: EventVisibility;
  start_time: string;
  end_time?: string;
  venue_name?: string;
  venue_address?: string;
  ticket_url?: string;
  ticket_price?: string;
  notes_private?: string;
  created_by: string;
}

export async function createEvent(input: CreateEventInput | CreateEventInput[]): Promise<{ event: Event | null; error: string | null }> {
  if (Array.isArray(input)) {
    const { data, error } = await supabase
      .from('events')
      .insert(input)
      .select();
    if (error) return { event: null, error: error.message };
    return { event: data?.[0] ?? null, error: null };
  }

  const { data, error } = await supabase
    .from('events')
    .insert(input)
    .select()
    .single();

  if (error) return { event: null, error: error.message };
  return { event: data, error: null };
}

type UpdateEventInput = {
  [K in keyof CreateEventInput]?: CreateEventInput[K] | null;
};

export async function updateEvent(
  eventId: string,
  updates: UpdateEventInput
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('events')
    .update(updates)
    .eq('id', eventId);

  return { error: error?.message ?? null };
}

export async function deleteEvent(eventId: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from('events').delete().eq('id', eventId);
  return { error: error?.message ?? null };
}
